/**
 * Before After Image Block - Editor Component
 *
 * @package BrandyBlocks
 */

import { __ } from '@wordpress/i18n';
import {
	useBlockProps,
	InspectorControls,
	MediaUpload,
	MediaUploadCheck,
} from '@wordpress/block-editor';
import {
	PanelBody,
	Button,
	ToggleControl,
	TextControl,
	RangeControl,
	SelectControl,
	Placeholder,
} from '@wordpress/components';

/**
 * Image upload component for before/after images.
 *
 * @param {Object}   props          Component props.
 * @param {string}   props.label    Label for the upload button.
 * @param {Object}   props.image    Image object with id, url, alt.
 * @param {Function} props.onChange Callback when image changes.
 * @return {JSX.Element} Image upload component.
 */
function ImageUploader( { label, image, onChange } ) {
	return (
		<MediaUploadCheck>
			<MediaUpload
				onSelect={ ( media ) =>
					onChange( {
						id: media.id,
						url: media.url,
						alt: media.alt || '',
					} )
				}
				allowedTypes={ [ 'image' ] }
				value={ image?.id }
				render={ ( { open } ) => (
					<div className="brandy-before-after-image-upload">
						{ image?.url ? (
							<div className="brandy-before-after-image-preview">
								<img src={ image.url } alt={ image.alt } />
								<div className="brandy-before-after-image-actions">
									<Button variant="secondary" onClick={ open }>
										{ __( 'Replace', 'brandy-blocks' ) }
									</Button>
									<Button
										variant="link"
										isDestructive
										onClick={ () =>
											onChange( { id: 0, url: '', alt: '' } )
										}
									>
										{ __( 'Remove', 'brandy-blocks' ) }
									</Button>
								</div>
							</div>
						) : (
							<Button variant="secondary" onClick={ open }>
								{ label }
							</Button>
						) }
					</div>
				) }
			/>
		</MediaUploadCheck>
	);
}

/**
 * Edit component for Before After Image block.
 *
 * @param {Object}   props               Block props.
 * @param {Object}   props.attributes    Block attributes.
 * @param {Function} props.setAttributes Function to update attributes.
 * @return {JSX.Element} Editor component.
 */
export default function Edit( { attributes, setAttributes } ) {
	const {
		beforeImage,
		afterImage,
		sliderPosition,
		orientation,
		beforeLabel,
		afterLabel,
		showLabels,
	} = attributes;

	const blockProps = useBlockProps( {
		className: `brandy-before-after-image orientation-${ orientation }`,
	} );

	const hasImages = beforeImage?.url && afterImage?.url;

	return (
		<>
			<InspectorControls>
				<PanelBody
					title={ __( 'Images', 'brandy-blocks' ) }
					initialOpen={ true }
				>
					<div className="brandy-before-after-inspector-images">
						<p>{ __( 'Before Image', 'brandy-blocks' ) }</p>
						<ImageUploader
							label={ __( 'Select Before Image', 'brandy-blocks' ) }
							image={ beforeImage }
							onChange={ ( value ) =>
								setAttributes( { beforeImage: value } )
							}
						/>
						<p style={ { marginTop: '16px' } }>
							{ __( 'After Image', 'brandy-blocks' ) }
						</p>
						<ImageUploader
							label={ __( 'Select After Image', 'brandy-blocks' ) }
							image={ afterImage }
							onChange={ ( value ) =>
								setAttributes( { afterImage: value } )
							}
						/>
					</div>
				</PanelBody>

				<PanelBody
					title={ __( 'Slider Settings', 'brandy-blocks' ) }
					initialOpen={ true }
				>
					<SelectControl
						label={ __( 'Orientation', 'brandy-blocks' ) }
						value={ orientation }
						options={ [
							{ label: __( 'Horizontal', 'brandy-blocks' ), value: 'horizontal' },
							{ label: __( 'Vertical', 'brandy-blocks' ), value: 'vertical' },
						] }
						onChange={ ( value ) =>
							setAttributes( { orientation: value } )
						}
					/>
					<RangeControl
						label={ __( 'Initial Position (%)', 'brandy-blocks' ) }
						value={ sliderPosition }
						onChange={ ( value ) =>
							setAttributes( { sliderPosition: value } )
						}
						min={ 0 }
						max={ 100 }
					/>
				</PanelBody>

				<PanelBody
					title={ __( 'Labels', 'brandy-blocks' ) }
					initialOpen={ false }
				>
					<ToggleControl
						label={ __( 'Show Labels', 'brandy-blocks' ) }
						checked={ showLabels }
						onChange={ ( value ) =>
							setAttributes( { showLabels: value } )
						}
					/>
					{ showLabels && (
						<>
							<TextControl
								label={ __( 'Before Label', 'brandy-blocks' ) }
								value={ beforeLabel }
								onChange={ ( value ) =>
									setAttributes( { beforeLabel: value } )
								}
							/>
							<TextControl
								label={ __( 'After Label', 'brandy-blocks' ) }
								value={ afterLabel }
								onChange={ ( value ) =>
									setAttributes( { afterLabel: value } )
								}
							/>
						</>
					) }
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				{ ! hasImages ? (
					<Placeholder
						icon="format-image"
						label={ __( 'Before After Image', 'brandy-blocks' ) }
						instructions={ __(
							'Upload or select two images to compare.',
							'brandy-blocks'
						) }
					>
						<div className="brandy-before-after-placeholder-uploads">
							<ImageUploader
								label={ __( 'Select Before Image', 'brandy-blocks' ) }
								image={ beforeImage }
								onChange={ ( value ) =>
									setAttributes( { beforeImage: value } )
								}
							/>
							<ImageUploader
								label={ __( 'Select After Image', 'brandy-blocks' ) }
								image={ afterImage }
								onChange={ ( value ) =>
									setAttributes( { afterImage: value } )
								}
							/>
						</div>
					</Placeholder>
				) : (
					<div className="brandy-before-after-container">
						<div className="brandy-before-after-images">
							<img
								src={ afterImage.url }
								alt={ afterImage.alt }
								className="brandy-after-image"
							/>
							<img
								src={ beforeImage.url }
								alt={ beforeImage.alt }
								className="brandy-before-image"
								style={ {
									clipPath:
										orientation === 'horizontal'
											? `inset(0 ${ 100 - sliderPosition }% 0 0)`
											: `inset(0 0 ${ 100 - sliderPosition }% 0)`,
								} }
							/>
						</div>
						{ showLabels && (
							<>
								<span className="brandy-before-label">
									{ beforeLabel }
								</span>
								<span className="brandy-after-label">
									{ afterLabel }
								</span>
							</>
						) }
						<div
							className="brandy-slider-handle"
							style={
								orientation === 'horizontal'
									? { left: `${ sliderPosition }%` }
									: { top: `${ sliderPosition }%` }
							}
						>
							<div className="brandy-slider-line" />
							<div className="brandy-slider-button">
								<svg
									viewBox="0 0 24 24"
									width="24"
									height="24"
								>
									<path d="M9 6l-6 6 6 6M15 6l6 6-6 6" />
								</svg>
							</div>
						</div>
					</div>
				) }
			</div>
		</>
	);
}
